import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import API_BASE_URL from "../../../src/config";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import CropModal from "../CropModal";



const RoomsAndSpaces = forwardRef(({ stayId: propStayId }, ref) => {
  const [roomAvailability, setRoomAvailability] = useState({});
  const [dateRange, setDateRange] = useState([null, null]);
  const [isAvailable, setIsAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [showCrop, setShowCrop] = useState(false);
  const [cropRoomIndex, setCropRoomIndex] = useState(null);
  const [galleryCropFile, setGalleryCropFile] = useState(null);
  const [galleryCropRoomIndex, setGalleryCropRoomIndex] = useState(null);
  const [galleryCropQueue, setGalleryCropQueue] = useState([]);
  const [galleryCropIndex, setGalleryCropIndex] = useState(0);
  const [startDate, endDate] = dateRange;
  const [rooms, setRooms] = useState([
    {
      id: "",
      title: "",
      units: [],
      description: "",
      room_type: "",
      price_min: "",
      price_max: "",
      tax_per_night: "",
      cover_image: null,
      cover_image_preview: "",
      details: [""],
      benefits: [""],
      rooms_available: 1,
      max_guests: 0,
      bedrooms: 0,
      bathrooms: 0,
      beds: 0,
      max_adults: 0,
      max_children: 0,
      is_active: true,

      room_images: [],
      room_images_preview: [],
      existing_room_images: [],

    },
  ]);

  const parseArray = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [""];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [""];
    } catch (e) {
      return [""];
    }
  };
  const stayId = propStayId || localStorage.getItem("stayId");

  useEffect(() => {
    const fetchStayDetails = async () => {
      if (!stayId) return;

      try {
        const token = localStorage.getItem("accessToken") || "";
        const res = await axios.post(
          `${API_BASE_URL}stays/details`,
          { id: stayId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data.success && res.data.data.rooms) {
          const apiRooms = res.data.data.rooms.map((r) => ({
            id: r.id || "",
            title: r.title || "",
            units: r.units || [],
            description: r.description || "",
            room_type: r.room_type || "",
            price_min: r.price_min || "",
            price_max: r.price_max || "",
            tax_per_night: r.tax_per_night || "",

            cover_image: null,
            cover_image_preview: r.cover_image || "",
            existing_cover_image: r.cover_image || "",
            room_images: [], // new images (File)
            existing_room_images: (r.images || []).map(img => img.image), // OLD images
            room_images_preview: (r.images || []).map(img => img.image), // for UI

            details: parseArray(r.details),
            benefits: parseArray(r.benefits),
            rooms_available: r.rooms_available || 1,
            max_guests: r.max_guests || 0,
            bedrooms: r.bedrooms || 0,
            bathrooms: r.bathrooms || 0,
            beds: r.beds || 0,
            max_adults: r.max_adults || 0,
            max_children: r.max_children || 0,
            is_active: true,
          }));

          setRooms(apiRooms.length > 0 ? apiRooms : rooms);
        }
      } catch (err) {
        console.error("Error fetching stay details:", err);
      }
    };

    fetchStayDetails();
  }, [stayId]);

  useEffect(() => {
    if (!rooms) return;

    const availability = {};

    rooms.forEach((room, idx) => {
      const roomDatesMap = {};

      room.units?.forEach((unit) => {
        unit.availability?.forEach((av) => {
          const currentStatus = av.is_available ? "available" : "booked";
          const prevStatus = roomDatesMap[av.date];
          if (!prevStatus) roomDatesMap[av.date] = currentStatus;
          else if (prevStatus === "available" && currentStatus === "booked") {
            roomDatesMap[av.date] = "booked";
          }
        });
      });

      const datesArray = Object.keys(roomDatesMap).map((date) => ({
        date,
        status: roomDatesMap[date],
      }));
      availability[room.id || room.title || idx] = datesArray;
    });

    setRoomAvailability(availability);
  }, [rooms]);

  const tileClassName = (roomId, date) => {
    const bookings = roomAvailability[roomId] || [];
    const isoDate = date.toLocaleDateString("en-CA");
    const day = bookings.find((b) => b.date === isoDate);
    if (!day) return "";
    if (day.status === "booked") return "calendar-booked";
    if (day.status === "maintenance") return "calendar-maintenance";
    return "calendar-available";
  };

  useImperativeHandle(ref, () => ({
    getFormData: () => rooms,

    submit: async () => {
      if (!stayId) {
        alert("Stay ID not found. Please complete Basic Info first.");
        return;
      }

      try {
        const token = localStorage.getItem("accessToken") || "";

        const formData = new FormData();
        formData.append("stayId", stayId);

        const roomsPayload = rooms.map((r) => {
          const filteredDetails = Array.isArray(r.details)
            ? r.details.map(d => d.trim()).filter(Boolean)
            : [];

          const filteredBenefits = Array.isArray(r.benefits)
            ? r.benefits.map(b => b.trim()).filter(Boolean)
            : [];

          const {
            details,
            benefits,
            room_images,
            room_images_preview,
            cover_image,
            ...rest
          } = r;

          const payload = {
            ...rest,
            cover_image: r.cover_image ? r.cover_image : r.existing_cover_image || null,
            existing_room_images: r.existing_room_images || [],
            price_min: Number(r.price_min),
            tax_per_night: Number(r.tax_per_night),
            rooms_available: Number(r.rooms_available),
            max_guests: Number(r.max_guests),
            bedrooms: Number(r.bedrooms),
            bathrooms: Number(r.bathrooms),
            beds: Number(r.beds),
            max_adults: Number(r.max_adults),
            max_children: Number(r.max_children),
          };

          if (filteredDetails.length > 0) {
            payload.details = filteredDetails;
          }

          if (filteredBenefits.length > 0) {
            payload.benefits = filteredBenefits;
          }

          return payload;
        });
        formData.append("rooms", JSON.stringify(roomsPayload));

        rooms.forEach((room, index) => {
          if (room.cover_image instanceof File) {
            formData.append(`room_cover_${index}`, room.cover_image);
          }
        });

        rooms.forEach((room, index) => {
          if (room.room_images && room.room_images.length > 0) {
            room.room_images.forEach((file) => {
              formData.append(`room_gallery_${index}`, file);
            });
          }
        });


        const res = await axios.post(
          `${API_BASE_URL}stays/add-rooms`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        toast.success("Rooms saved successfully!");
        console.log("Response:", res.data);
      } catch (err) {
        console.error("Error saving rooms info:", err);
        toast.error("Failed to save rooms info.");
      }
    },

  }));

  const handleRoomChange = (index, field, value) => {
    const updatedRooms = [...rooms];
    updatedRooms[index][field] = value;
    setRooms(updatedRooms);
  };

  const addRoom = () => {
    setRooms([
      ...rooms,
      {
        id: "",
        units: [],
        title: "",
        description: "",
        room_type: "",
        price_min: "",
        price_max: "",
        tax_per_night: "",
        cover_image: null,
        cover_image_preview: "",
        details: [""],
        benefits: [""],
        rooms_available: 1,
        max_guests: 0,
        bedrooms: 0,
        bathrooms: 0,
        beds: 0,
        max_adults: 0,
        max_children: 0,
        is_active: true,
        room_images: [],
        room_images_preview: [],
        existing_room_images: [],
      },
    ]);
  };

  const removeRoom = (index) => {
    const updatedRooms = rooms.filter((_, i) => i !== index);
    setRooms(updatedRooms);
  };

  const handleAddItem = (index, field) => {
    const updatedRooms = [...rooms];
    if (!Array.isArray(updatedRooms[index][field])) {
      updatedRooms[index][field] = [""];
    }
    updatedRooms[index][field].push("");
    setRooms(updatedRooms);
  };

  const handleItemChange = (index, field, subIndex, value) => {
    const updatedRooms = [...rooms];
    if (!Array.isArray(updatedRooms[index][field])) {
      updatedRooms[index][field] = [""];
    }
    updatedRooms[index][field][subIndex] = value;
    setRooms(updatedRooms);
  };

  const handleFileChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCropRoomIndex(index);
    setCropFile(file);
    setShowCrop(true);
  };

  const handleRoomCropComplete = (blob) => {
    if (cropRoomIndex === null) return;

    const file = new File([blob], `room_${cropRoomIndex}.jpg`, {
      type: blob.type || "image/jpeg",
    });

    const updatedRooms = [...rooms];
    updatedRooms[cropRoomIndex].cover_image = file;
    updatedRooms[cropRoomIndex].cover_image_preview = URL.createObjectURL(file);

    setRooms(updatedRooms);

    setCropRoomIndex(null);
    setCropFile(null);
    setShowCrop(false);
  };

  // const handleRoomImagesChange = (index, e) => {
  //   const files = Array.from(e.target.files);
  //   if (!files.length) return;

  //   setGalleryCropRoomIndex(index);
  //   setGalleryCropQueue(files);
  //   setGalleryCropIndex(0);
  //   setGalleryCropFile(files[0]);
  //   setShowCrop(true);
  // };

  const handleRoomImagesChange = (index, e) => {
    const files = Array.from(e.target.files);

    // Reset input so same file can be selected again
    e.target.value = "";

    if (!files.length) return;

    const MAX_SIZE = 2 * 1024 * 1024; // 2MB

    // ✅ Validate files
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not a valid image`);
        return false;
      }

      if (file.size > MAX_SIZE) {
        toast.error(`${file.name} exceeds 2MB size limit`);
        return false;
      }

      return true;
    });

    if (!validFiles.length) return;

    // ✅ Start crop queue
    setGalleryCropRoomIndex(index);
    setGalleryCropQueue(validFiles);
    setGalleryCropIndex(0);
    setGalleryCropFile(validFiles[0]); // first image
    setShowCrop(true);
  };

  const handleGalleryCropComplete = (blob) => {
    if (galleryCropRoomIndex === null) return;

    const file = new File(
      [blob],
      `room_gallery_${Date.now()}.jpg`,
      { type: blob.type || "image/jpeg" }
    );

    const updatedRooms = [...rooms];

    updatedRooms[galleryCropRoomIndex].room_images.push(file);
    updatedRooms[galleryCropRoomIndex].room_images_preview.push(
      URL.createObjectURL(file)
    );

    setRooms(updatedRooms);

    // 👉 Process next image in queue
    const nextIndex = galleryCropIndex + 1;

    if (nextIndex < galleryCropQueue.length) {
      setGalleryCropIndex(nextIndex);
      setGalleryCropFile(galleryCropQueue[nextIndex]);
    } else {
      // done
      setShowCrop(false);
      setGalleryCropFile(null);
      setGalleryCropQueue([]);
      setGalleryCropIndex(0);
      setGalleryCropRoomIndex(null);
    }
  };

  const handleSubmitAvailability = async ({ roomId }) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      console.log("Please select a date range first.");
      return;
    }

    const payload = {
      roomId,
      start_date: dateRange[0].toLocaleDateString("en-CA"),
      end_date: dateRange[1].toLocaleDateString("en-CA"),
      is_available: isAvailable,
    };

    try {
      const token = localStorage.getItem("accessToken") || "";
      setLoading(true);
      const res = await axios.post(
        `${API_BASE_URL}stays/availability`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("API Response:", res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  const removeGalleryImage = (roomIndex, imgIndex) => {
    const updatedRooms = [...rooms];

    const preview = updatedRooms[roomIndex].room_images_preview[imgIndex];

    // If image is from existing images
    if (updatedRooms[roomIndex].existing_room_images.includes(preview)) {
      updatedRooms[roomIndex].existing_room_images =
        updatedRooms[roomIndex].existing_room_images.filter(
          (img) => img !== preview
        );
    } else {
      // If image is newly added
      const newImagesOnly = updatedRooms[roomIndex].room_images_preview.filter(
        (img) =>
          !updatedRooms[roomIndex].existing_room_images.includes(img)
      );

      const newIndex = newImagesOnly.indexOf(preview);

      if (newIndex > -1) {
        updatedRooms[roomIndex].room_images.splice(newIndex, 1);
      }
    }

    // Remove from preview
    updatedRooms[roomIndex].room_images_preview.splice(imgIndex, 1);

    setRooms(updatedRooms);
  };


  const handleRemoveItem = (index, field, subIndex) => {
    const updatedRooms = [...rooms];

    if (!Array.isArray(updatedRooms[index][field])) return;

    updatedRooms[index][field].splice(subIndex, 1);

    setRooms(updatedRooms);
  };

  const blockInvalidNumberInput = (e) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleNumberInput = (e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, "");
  };

  return (
    <div className="room-section">
      {showCrop && (
        <CropModal
          file={galleryCropFile || cropFile}
          onClose={() => {
            setShowCrop(false);
            setGalleryCropQueue([]);
            setGalleryCropFile(null);
            setCropFile(null);
          }}
          onComplete={(blob) => {
            if (galleryCropFile) {
              handleGalleryCropComplete(blob);
            } else {
              handleRoomCropComplete(blob);
            }
          }}
        />
      )}


      <h2 className="mb-3">Rooms & Spaces</h2>
      <p className="mb-4">
        Add all room types available in this property with details and price
        range.
      </p>

      {rooms.map((room, index) => (
        <div key={index} className="card p-3 border rounded">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Room {index + 1}</h5>
            {rooms.length > 1 && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => removeRoom(index)}
              >
                Remove
              </button>
            )}
          </div>

          <div className="row gx-3 gy-3">
            {/* Title & Type */}
            <div className="col-md-6">
              <div className="form-group">
                <label>Room Title</label>
                <div className="formgroupinner">
                  <input
                    type="text"
                    className="form-control"
                    value={room.title === "0" ? "" : room.title}
                    onChange={(e) =>
                      handleRoomChange(index, "title", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Room Type</label>
                <div className="formgroupinner">
                  <select
                    className="form-select"
                    value={room.room_type}
                    onChange={(e) =>
                      handleRoomChange(index, "room_type", e.target.value)
                    }
                  >
                    <option value="">Select Room Type</option>
                    <option value="Single">Single</option>
                    <option value="Double">Double</option>
                    <option value="Suite">Suite</option>
                    <option value="Deluxe">Deluxe</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label>Room Cover Image</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => handleFileChange(index, e)}
                />
                {/* {room.cover_image_preview && (
                  <img
                    src={room.cover_image_preview}
                    alt="Preview"
                    style={{ width: "120px", marginTop: "10px", borderRadius: "8px" }}
                  />
                )} */}
                {room.cover_image_preview && (
                  <div style={{ position: "relative", display: "inline-block", marginTop: "10px" }}>
                    <img
                      src={room.cover_image_preview}
                      alt="Preview"
                      style={{ width: "120px", borderRadius: "8px" }}
                    />

                    <span
                      onClick={() => {
                        const updatedRooms = [...rooms];
                        updatedRooms[index].cover_image = null;
                        updatedRooms[index].cover_image_preview = "";
                        // updatedRooms[index].existing_cover_image = "";
                        updatedRooms[index].existing_cover_image = null;
                        setRooms(updatedRooms);
                      }}
                      style={{
                        position: "absolute",
                        top: "-5px",
                        right: "-5px",
                        background: "#fff",
                        border: "1px solid #070707",
                        color: "red",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: 600
                      }}
                    >
                      ✕
                    </span>
                  </div>
                )}

              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label>Room Images (Gallery)</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleRoomImagesChange(index, e)}
                />

                {/* Preview */}
                <div className="d-flex flex-wrap mt-2 gap-2">
                  {room.room_images_preview?.map((img, i) => (
                    <div
                      key={i}
                      style={{
                        position: "relative",
                        width: "80px",
                        height: "80px",
                      }}
                    >
                      <img
                        src={img}
                        alt="Room"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                      <span
                        onClick={() => removeGalleryImage(index, i)}
                        style={{
                          position: "absolute",
                          top: "-5px",
                          right: "-5px",
                          background: "#fff",
                          border: "1px solid #070707",
                          color: "red",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          fontWeight: 600
                        }}
                      >
                        ✕
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Room Inventory */}
            <div className="col-md-12 mt-3">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Number of Rooms *</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control"
                      placeholder="How many rooms of this type"
                      onKeyDown={blockInvalidNumberInput}
                      onInput={handleNumberInput}
                      value={room.rooms_available}
                      onChange={(e) =>
                        handleRoomChange(index, "rooms_available", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Guest Capacity */}
            <div className="col-md-12 mt-3">
              <div className="row">

                <div className="col-md-4">
                  <div className="form-group">
                    <label>Max Guests</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      placeholder="Total allowed guests"
                      onKeyDown={blockInvalidNumberInput}
                      onInput={handleNumberInput}
                      value={room.max_guests}
                      onChange={(e) =>
                        handleRoomChange(index, "max_guests", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label>Max Adults</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      placeholder="Allowed adults"
                      onKeyDown={blockInvalidNumberInput}
                      onInput={handleNumberInput}
                      value={room.max_adults}
                      onChange={(e) =>
                        handleRoomChange(index, "max_adults", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label>Max Children</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      placeholder="Allowed children"
                      onKeyDown={blockInvalidNumberInput}
                      onInput={handleNumberInput}
                      value={room.max_children}
                      onChange={(e) =>
                        handleRoomChange(index, "max_children", e.target.value)
                      }
                    />
                  </div>
                </div>

              </div>
            </div>

            {/* Room Configuration */}
            <div className="col-md-12 mt-3">
              <div className="row">
                {/* Bedrooms */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Bedrooms</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      onKeyDown={blockInvalidNumberInput}
                      onInput={handleNumberInput}
                      value={room.bedrooms}
                      onChange={(e) =>
                        handleRoomChange(index, "bedrooms", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Bathrooms */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Bathrooms</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      onKeyDown={blockInvalidNumberInput}
                      onInput={handleNumberInput}
                      value={room.bathrooms}
                      onChange={(e) =>
                        handleRoomChange(index, "bathrooms", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Beds */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Beds</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      onKeyDown={blockInvalidNumberInput}
                      onInput={handleNumberInput}
                      value={room.beds}
                      onChange={(e) =>
                        handleRoomChange(index, "beds", e.target.value)
                      }
                    />
                  </div>
                </div>

              </div>
            </div>

            {/*. Price Fields */}
            <div className="col-md-6">
              <div className="form-group">
                <label>Price per night</label>
                <input
                  type="number"
                  min="1"
                  className="form-control"
                  placeholder="e.g., 1000"
                  onKeyDown={blockInvalidNumberInput}
                  onInput={handleNumberInput}
                  value={room.price_min}
                  onChange={(e) =>
                    handleRoomChange(index, "price_min", e.target.value)
                  }
                />
              </div>
            </div>

            {/* Description */}
            <div className="col-md-12">
              <div className="form-group">
                <label>Description</label>
                <div className="formgroupinner">
                  <textarea
                    className="form-control"
                    rows={2}
                    value={room.description === "0" ? "" : room.description}
                    onChange={(e) =>
                      handleRoomChange(index, "description", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/*. Safe Mapping for Details */}
            <div className="col-md-8">
              <div className="formgroupinner">
                <label>Room Details</label>
                <div className="formgroupinner mt-3">
                  {parseArray(room.details).map((item, i) => (
                    <div
                      key={i}
                      style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                    >
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., Sea View Balcony"
                        value={item}
                        onChange={(e) =>
                          handleItemChange(index, "details", i, e.target.value)
                        }
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index, "details", i)}
                        className="btn btn-danger btn-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="theme-btn btn-sm"
                onClick={() => handleAddItem(index, "details")}
              >
                + Add Detail
              </button>
            </div>

            {/*. Safe Mapping for Benefits */}
            <div className="col-md-8">
              <div className="formgroupinner">
                <label>Room Benefits</label>
                <div className="formgroupinner mt-3">
                  {parseArray(room.benefits).map((item, i) => (
                    <div
                      key={i}
                      style={{ display: "flex", gap: "8px", marginBottom: "8px" }}
                    >
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., Free Breakfast"
                        value={item}
                        onChange={(e) =>
                          handleItemChange(index, "benefits", i, e.target.value)
                        }
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index, "benefits", i)}
                        className="btn btn-danger btn-sm"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                className="theme-btn btn-sm"
                onClick={() => handleAddItem(index, "benefits")}
              >
                + Add Benefit
              </button>
            </div>
          </div>
        </div>
      ))}

      <button type="button" className="theme-btn" onClick={addRoom}>
        + Add Another Room
      </button>
    </div>
  );
});

export default RoomsAndSpaces;